<script lang="ts">
  import { page } from '$app/stores'
  import { utils } from '$lib/utils'
  import type { StudioIncludes } from '@bond/bruce-api/api/studio/response'
  import { Icon, Skeleton, type Language } from '@bond/components'

  export let studio:
    | StudioIncludes<{ params: { include_categories: true } }>
    | StudioIncludes<{ params: object }>
  export let vicinity = false
  export let rating = false
  export let categories = false
  export let description = false
  export let imageUrl: string
  export let size: 'sm' | 'md' = 'md'
  export let opacity = 70

  let image: HTMLImageElement | undefined = undefined
  let imgLoading = true
  $: lang = $page.params.lang as Language
  $: localeRoute = utils.getRoute(lang)

  $: if (image?.complete) {
    imgLoading = false
  }
</script>

<a
  href={localeRoute(
    `/studios/${studio.id}/${utils.toSpeakingUrl(studio.name)}`,
  )}
  class={size}
  class:description
>
  <div class="image-container">
    <img
      src={`${imageUrl}/fl_progressive,w_568,h_355,c_fill,b_black,o_${opacity}/${studio.photo}`}
      alt={studio.name}
      bind:this={image}
      on:load={() => (imgLoading = false)}
      class:imgLoading
      loading="lazy"
    />
    <Skeleton visible={imgLoading} />
    {#if rating && studio.rating}
      <div class="rating">
        <Icon size={1} icon="ph-star-fill" />
        {Math.floor(studio.rating.average / 10) / 10}
      </div>
    {/if}
    <div class="title">
      <h5 class:description>{studio.name}</h5>
      {#if vicinity && studio.vicinity}
        <div>{studio.vicinity}</div>
      {/if}
    </div>
    {#if categories && 'categories' in studio}
      {@const categories = studio.categories
        .map((category) => category.title)
        .join(', ')}
      <div class="categories">{categories}</div>
    {/if}
  </div>
  {#if description && studio.description}
    <div class="description">
      <p>
        <!-- Not using Svelte Markdown due to maximum call size -->
        {studio.description}
      </p>
    </div>
  {/if}
</a>

<style lang="scss">
  @use '@bond/assets/css/breakpoints';

  a {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100%;
    border-radius: 0.75em;
    overflow: hidden;
    text-decoration: none;

    &.description {
      border: 1px solid rgba(var(--color-on-background-rgb), 0.2);
    }

    .image-container {
      position: relative;
      height: calc(100% - 20px);
      width: calc(100% - 32px);
      padding: 10px 16px;
      aspect-ratio: 16 / 10;
      color: #fff;
      --icon-color: #fff;

      img {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        object-fit: cover;
        opacity: 1;
        transition:
          opacity 175ms ease-in,
          transform 0.5s;
        transform: scale(1);

        &:hover {
          transform: scale(1.05);
        }

        &.imgLoading {
          opacity: 0;
        }
      }

      .rating {
        pointer-events: none;
        position: absolute;
        display: flex;
        align-items: center;
        opacity: 0.87;
        font-size: 12px;
      }

      .title {
        pointer-events: none;
        position: absolute;
        left: 16px;
        top: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: calc(100% - 16px * 2);
        height: 100%;
        gap: 0.125em;
        text-align: center;

        h5 {
          font-size: 18px;
          margin: 0;

          @media screen and (max-width: breakpoints.$breakpoint-sm) {
            font-size: 16px;
          }
        }

        h5.description {
          @media screen and (max-width: breakpoints.$breakpoint-sm) {
            font-size: 18px;
          }
        }

        div {
          font-size: 12px;
        }
      }

      .categories {
        pointer-events: none;
        position: absolute;
        width: calc(100% - 16px * 2);
        bottom: 10px;
        opacity: 0.87;
        font-size: 12px;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }

    div.description {
      margin: 0;
      padding: 1em;

      p {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        line-clamp: 5;
        -webkit-line-clamp: 5;
        margin: 0;
        font-size: 16px;
      }
    }
  }
</style>
